import { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import api from '../../lib/api';
import { PAGE_CONTENT_MAX_CLASS } from '../../lib/pageLayout';
import {
  FLUID_SILENCE_MS_DEFAULT,
  FLUID_SILENCE_MS_MAX,
  FLUID_SILENCE_MS_MIN,
  parseFluidSilenceMsFromApi,
} from '../../lib/fluidSilenceMs';

function configErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object' && 'response' in err) {
    const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
    if (typeof msg === 'string' && msg.trim()) return msg;
  }
  return fallback;
}

function EyeIcon({ open }: { open: boolean }) {
  if (open) {
    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-4 w-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.477 0 8.268 2.943 9.542 7-1.274 4.057-5.065 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    );
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="h-4 w-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.477 0-8.268-2.943-9.542-7a9.956 9.956 0 012.223-3.592M6.228 6.228A9.956 9.956 0 0112 5c4.477 0 8.268 2.943 9.542 7a9.96 9.96 0 01-4.342 5.346M6.228 6.228L3 3m3.228 3.228l3.65 3.65M17.772 17.772l3.228 3.228m-3.228-3.228l-3.65-3.65"
      />
    </svg>
  );
}

function PasswordInput({
  value,
  onChange,
  placeholder,
  id,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  id?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        id={id}
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        className="absolute right-1 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-2"
        tabIndex={-1}
        aria-label={show ? 'Hide key' : 'Show key'}
      >
        <EyeIcon open={show} />
      </button>
    </div>
  );
}

export function AdminSettingsPage() {
  // LLM
  const [llmProvider, setLlmProvider] = useState('anthropic');
  const [llmApiKeyAnthropic, setLlmApiKeyAnthropic] = useState('');
  const [llmApiKeyOpenai, setLlmApiKeyOpenai] = useState('');
  const [llmModel, setLlmModel] = useState('');
  const [llmProviders, setLlmProviders] = useState<string[]>(['anthropic', 'openai']);
  const [llmModelsMap, setLlmModelsMap] = useState<Record<string, string[]>>({
    anthropic: ['claude-sonnet-4-20250514'],
    openai: ['gpt-4o'],
  });

  // TTS
  const [provider, setProvider] = useState('browser');
  const [apiKey, setApiKey] = useState('');
  const [voice, setVoice] = useState('nova');
  const [voices, setVoices] = useState<string[]>([]);
  const [ttsModel, setTtsModel] = useState('tts-1');
  const [ttsModels, setTtsModels] = useState<string[]>([]);
  const [speechMode, setSpeechMode] = useState('fluid');
  const [fluidSilenceMs, setFluidSilenceMs] = useState(FLUID_SILENCE_MS_DEFAULT);

  // Platform persona / coaching (practice LLM behavior)
  const [personaSystemPromptPrefix, setPersonaSystemPromptPrefix] = useState('');
  const [personaSystemPromptSuffix, setPersonaSystemPromptSuffix] = useState('');
  const [personaBuyerDifficulty, setPersonaBuyerDifficulty] = useState('standard');
  const [personaToneFormality, setPersonaToneFormality] = useState('neutral');
  const [personaResponseVerbosity, setPersonaResponseVerbosity] = useState('standard');
  const [personaDirectorRules, setPersonaDirectorRules] = useState('');
  const [personaOrgContextEnabled, setPersonaOrgContextEnabled] = useState('true');
  const [coachingHintStyle, setCoachingHintStyle] = useState('concise');
  const [personaEvaluationSuffix, setPersonaEvaluationSuffix] = useState('');
  const [personaSafetyNote, setPersonaSafetyNote] = useState('');

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .get('/admin/config')
      .then((res) => {
        setError(null);
        const data = res.data;

        // LLM
        const providers = safeJsonParse<string[]>(data.llm_providers, ['anthropic', 'openai']);
        const modelsMap = safeJsonParse<Record<string, string[]>>(data.llm_models, {});
        setLlmProviders(providers);
        setLlmModelsMap(modelsMap);
        const lp = data.llm_provider || 'anthropic';
        setLlmProvider(lp);
        setLlmApiKeyAnthropic(data.llm_api_key_anthropic || '');
        setLlmApiKeyOpenai(data.llm_api_key_openai || '');
        const defaultModel = (modelsMap[lp] ?? [])[0] ?? '';
        setLlmModel(data.llm_model || defaultModel);

        // TTS
        setProvider(data.tts_provider || 'browser');
        setApiKey(data.tts_api_key || '');
        setVoice(data.tts_voice || 'nova');
        setTtsModel(data.tts_model || 'tts-1');
        setSpeechMode(data.speech_mode || 'fluid');
        setFluidSilenceMs(parseFluidSilenceMsFromApi(data.fluid_silence_ms));
        setVoices(safeJsonParse<string[]>(data.openai_voices, []));
        setTtsModels(safeJsonParse<string[]>(data.openai_tts_models, []));

        setPersonaSystemPromptPrefix(data.persona_system_prompt_prefix || '');
        setPersonaSystemPromptSuffix(data.persona_system_prompt_suffix || '');
        setPersonaBuyerDifficulty(data.persona_buyer_difficulty || 'standard');
        setPersonaToneFormality(data.persona_tone_formality || 'neutral');
        setPersonaResponseVerbosity(data.persona_response_verbosity || 'standard');
        setPersonaDirectorRules(data.persona_director_rules || '');
        setPersonaOrgContextEnabled(data.persona_org_context_enabled || 'true');
        setCoachingHintStyle(data.coaching_hint_style || 'concise');
        setPersonaEvaluationSuffix(data.persona_evaluation_suffix || '');
        setPersonaSafetyNote(data.persona_safety_note || '');
      })
      .catch((err: unknown) => {
        const msg = configErrorMessage(err, 'Could not load platform settings.');
        setError(msg);
        Sentry.captureException(err, {
          tags: { area: 'admin_settings' },
          extra: { phase: 'load' },
        });
      });
  }, []);

  // When provider changes, reset model to first option for that provider
  const handleLlmProviderChange = (p: string) => {
    setLlmProvider(p);
    const models = llmModelsMap[p] ?? [];
    if (!models.includes(llmModel)) {
      setLlmModel(models[0] ?? '');
    }
  };

  const currentLlmApiKey = llmProvider === 'anthropic' ? llmApiKeyAnthropic : llmApiKeyOpenai;
  const setCurrentLlmApiKey = (v: string) => {
    if (llmProvider === 'anthropic') setLlmApiKeyAnthropic(v);
    else setLlmApiKeyOpenai(v);
  };

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await api.put('/admin/config', {
        llm_provider: llmProvider,
        llm_api_key_anthropic: llmApiKeyAnthropic,
        llm_api_key_openai: llmApiKeyOpenai,
        llm_model: llmModel,
        tts_provider: provider,
        tts_api_key: apiKey,
        tts_voice: voice,
        tts_model: ttsModel,
        speech_mode: speechMode,
        fluid_silence_ms: String(fluidSilenceMs),
        persona_system_prompt_prefix: personaSystemPromptPrefix,
        persona_system_prompt_suffix: personaSystemPromptSuffix,
        persona_buyer_difficulty: personaBuyerDifficulty,
        persona_tone_formality: personaToneFormality,
        persona_response_verbosity: personaResponseVerbosity,
        persona_director_rules: personaDirectorRules,
        persona_org_context_enabled: personaOrgContextEnabled,
        coaching_hint_style: coachingHintStyle,
        persona_evaluation_suffix: personaEvaluationSuffix,
        persona_safety_note: personaSafetyNote,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: unknown) {
      const msg = configErrorMessage(err, 'Could not save platform settings.');
      setError(msg);
      Sentry.captureException(err, { tags: { area: 'admin_settings' }, extra: { phase: 'save' } });
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    const testText = `Hi, I'm your practice persona. This is how I'll sound during video call sessions.`;

    if (provider === 'browser') {
      if (!window.speechSynthesis) {
        setTesting(false);
        return;
      }
      const utterance = new SpeechSynthesisUtterance(testText);
      utterance.onend = () => setTesting(false);
      utterance.onerror = () => setTesting(false);
      window.speechSynthesis.speak(utterance);
      return;
    }

    try {
      const response = await api.post(
        '/practice/tts',
        { text: testText },
        { responseType: 'blob' },
      );

      if (response.data instanceof Blob && response.data.type.includes('audio')) {
        const url = URL.createObjectURL(response.data);
        const audio = new Audio(url);
        audio.onended = () => {
          URL.revokeObjectURL(url);
          setTesting(false);
        };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          setTesting(false);
        };
        await audio.play();
      } else {
        setTesting(false);
      }
    } catch {
      setTesting(false);
    }
  };

  const currentLlmModels = llmModelsMap[llmProvider] ?? [];

  return (
    <div className={PAGE_CONTENT_MAX_CLASS}>
      <h1 className="text-2xl font-bold text-gray-900 mb-2">Platform Settings</h1>
      <p className="text-gray-600 mb-8">
        Configure the AI model provider, text-to-speech, and other platform-wide settings.
      </p>

      {error && (
        <div
          className="mb-6 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          {error}
        </div>
      )}

      {/* LLM Provider */}
      <div className="bg-white rounded-lg border p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">AI Model Provider</h2>
        <p className="text-sm text-gray-500 mb-4">
          Used for all practice session personas, director decisions, and evaluations.
        </p>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
            <select
              value={llmProvider}
              onChange={(e) => handleLlmProviderChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {llmProviders.map((p) => (
                <option key={p} value={p}>
                  {p === 'anthropic' ? 'Anthropic' : p === 'openai' ? 'OpenAI' : p}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
            <PasswordInput
              value={currentLlmApiKey}
              onChange={setCurrentLlmApiKey}
              placeholder={llmProvider === 'anthropic' ? 'sk-ant-...' : 'sk-...'}
            />
            <p className="text-xs text-gray-400 mt-1">
              Each provider's key is saved independently.
            </p>
          </div>

          {currentLlmModels.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Model</label>
              <select
                value={llmModel}
                onChange={(e) => setLlmModel(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                {currentLlmModels.map((m) => (
                  <option key={m} value={m}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Practice buyer AI & coaching */}
      <div className="bg-white rounded-lg border p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Practice buyer AI behavior</h2>
        <p className="text-sm text-gray-500 mb-4">
          Tune how simulated buyers respond during practice, how the committee director picks
          speakers, whether org document context is injected, and how coaching hints and session
          evaluations are phrased. Empty text fields clear saved values.
        </p>

        <div className="space-y-4">
          <div>
            <label
              htmlFor="persona_system_prompt_prefix"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Persona system prompt — prefix
            </label>
            <textarea
              id="persona_system_prompt_prefix"
              value={personaSystemPromptPrefix}
              onChange={(e) => setPersonaSystemPromptPrefix(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-sm"
              placeholder="Optional text placed at the very start of the buyer system prompt."
            />
            <p className="text-xs text-gray-400 mt-1">
              Prepended before the role-play instructions for every persona reply. Use for global
              framing (e.g. industry, compliance tone).
            </p>
          </div>

          <div>
            <label
              htmlFor="persona_system_prompt_suffix"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Persona system prompt — suffix
            </label>
            <textarea
              id="persona_system_prompt_suffix"
              value={personaSystemPromptSuffix}
              onChange={(e) => setPersonaSystemPromptSuffix(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 font-mono text-sm"
              placeholder="Optional text appended at the end of the buyer system prompt."
            />
            <p className="text-xs text-gray-400 mt-1">
              Appended after difficulty, length, formality, and safety notes. Use for extra rules
              that apply to all catalog personas.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Buyer pushback (difficulty)
              </label>
              <select
                value={personaBuyerDifficulty}
                onChange={(e) => setPersonaBuyerDifficulty(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="easy">Easier — more collaborative</option>
                <option value="standard">Standard (default)</option>
                <option value="hard">Harder — more demanding</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Controls how tough objections are and how fast resistance escalates.
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Communication formality
              </label>
              <select
                value={personaToneFormality}
                onChange={(e) => setPersonaToneFormality(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="casual">Casual</option>
                <option value="neutral">Neutral (default)</option>
                <option value="formal">Formal</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Guides spoken/written tone for the buyer (slang vs. professional).
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Response length
              </label>
              <select
                value={personaResponseVerbosity}
                onChange={(e) => setPersonaResponseVerbosity(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
              >
                <option value="brief">Brief</option>
                <option value="standard">Standard (default)</option>
                <option value="verbose">More verbose</option>
              </select>
              <p className="text-xs text-gray-400 mt-1">
                Target length for chat practice and video call sessions (sentence counts in the
                prompt).
              </p>
            </div>
          </div>

          <div>
            <label
              htmlFor="persona_safety_note"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Safety and boundaries (buyer)
            </label>
            <textarea
              id="persona_safety_note"
              value={personaSafetyNote}
              onChange={(e) => setPersonaSafetyNote(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              placeholder="e.g. Do not simulate harassment or illegal activity."
            />
            <p className="text-xs text-gray-400 mt-1">
              Optional block labeled &quot;Safety and boundaries&quot; inside the buyer system
              prompt, before the suffix.
            </p>
          </div>

          <div>
            <label
              htmlFor="persona_director_rules"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Buying committee director — extra rules
            </label>
            <textarea
              id="persona_director_rules"
              value={personaDirectorRules}
              onChange={(e) => setPersonaDirectorRules(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              placeholder="Only applies when multiple personas are in a session."
            />
            <p className="text-xs text-gray-400 mt-1">
              Appended to the director model that chooses which buyer speaks next in multi-persona
              sessions.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Organization document context (RAG)
            </label>
            <select
              value={personaOrgContextEnabled}
              onChange={(e) => setPersonaOrgContextEnabled(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="true">Enabled — inject relevant org chunks into buyer prompt</option>
              <option value="false">Disabled — no semantic search / org context for buyers</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              When enabled, each user message can add &quot;Organization Context&quot; from embedded
              documents. Disabling skips retrieval and embedding logging for that step.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Real-time coaching hints
            </label>
            <select
              value={coachingHintStyle}
              onChange={(e) => setCoachingHintStyle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="concise">Concise (1–2 sentences)</option>
              <option value="detailed">Detailed (2–4 sentences, may include examples)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              Shown as hints on the last assistant message when a practice goal is set.
            </p>
          </div>

          <div>
            <label
              htmlFor="persona_evaluation_suffix"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Session evaluation — extra guidance
            </label>
            <textarea
              id="persona_evaluation_suffix"
              value={personaEvaluationSuffix}
              onChange={(e) => setPersonaEvaluationSuffix(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              placeholder="e.g. Emphasize discovery questions and talk track adherence."
            />
            <p className="text-xs text-gray-400 mt-1">
              Appended to the coach evaluation prompt when a session ends with a goal (JSON
              score/summary/strengths/improvements).
            </p>
          </div>
        </div>
      </div>

      {/* Speech Mode */}
      <div className="bg-white rounded-lg border p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Speech Mode</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Video Call behavior
            </label>
            <select
              value={speechMode}
              onChange={(e) => setSpeechMode(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="fluid">Fluid Conversation (always-on mic, auto-detect pauses)</option>
              <option value="turn-by-turn">Turn-by-Turn (push-to-talk)</option>
            </select>
            <p className="text-xs text-gray-400 mt-1">
              {speechMode === 'fluid'
                ? 'Mic stays on during the session. Pauses are detected automatically and the user can interrupt the AI mid-speech.'
                : 'User clicks a button or holds spacebar to speak, then releases to send.'}
            </p>
          </div>

          {speechMode === 'fluid' && (
            <div>
              <label
                htmlFor="fluid-silence-ms"
                className="block text-sm font-medium text-gray-700 mb-1"
              >
                Pause before sending (fluid mode)
              </label>
              <div className="flex flex-wrap items-center gap-2">
                <input
                  id="fluid-silence-ms"
                  type="number"
                  min={FLUID_SILENCE_MS_MIN}
                  max={FLUID_SILENCE_MS_MAX}
                  step={100}
                  value={fluidSilenceMs}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isFinite(v)) {
                      setFluidSilenceMs(FLUID_SILENCE_MS_DEFAULT);
                      return;
                    }
                    setFluidSilenceMs(
                      Math.min(FLUID_SILENCE_MS_MAX, Math.max(FLUID_SILENCE_MS_MIN, v)),
                    );
                  }}
                  className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <span className="text-sm text-gray-600">ms</span>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                After speech recognition finalizes a phrase, wait this long of quiet time before
                your message is sent. Higher values reduce the AI jumping in during short thinking
                pauses ({FLUID_SILENCE_MS_MIN}–{FLUID_SILENCE_MS_MAX} ms).
              </p>
            </div>
          )}
        </div>
      </div>

      {/* TTS */}
      <div className="bg-white rounded-lg border p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Text-to-Speech (TTS)</h2>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Provider</label>
            <select
              value={provider}
              onChange={(e) => setProvider(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              <option value="browser">Browser Native (free, lower quality)</option>
              <option value="openai">OpenAI TTS (natural, paid)</option>
            </select>
          </div>

          {provider === 'openai' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  OpenAI API Key
                </label>
                <PasswordInput value={apiKey} onChange={setApiKey} placeholder="sk-..." />
                <p className="text-xs text-gray-400 mt-1">Used only for text-to-speech synthesis</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Voice</label>
                <select
                  value={voice}
                  onChange={(e) => setVoice(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {(voices.length > 0
                    ? voices
                    : ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer']
                  ).map((v) => (
                    <option key={v} value={v}>
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quality</label>
                <select
                  value={ttsModel}
                  onChange={(e) => setTtsModel(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  {(ttsModels.length > 0 ? ttsModels : ['tts-1', 'tts-1-hd']).map((m) => (
                    <option key={m} value={m}>
                      {m === 'tts-1'
                        ? 'Standard (tts-1) — faster'
                        : 'HD (tts-1-hd) — higher quality'}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  HD produces clearer audio but adds slight latency.
                </p>
              </div>
            </>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-green-700 text-white px-6 py-2 rounded-md font-medium hover:bg-green-800 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save'}
            </button>
            <button
              onClick={handleTest}
              disabled={testing}
              className="bg-gray-100 text-gray-700 px-6 py-2 rounded-md font-medium hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              {testing ? 'Playing...' : 'Test Voice'}
            </button>
            {saved && (
              <span className="text-sm text-green-600 font-medium">Saved successfully</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function safeJsonParse<T>(value: unknown, fallback: T): T {
  if (typeof value !== 'string') return fallback;
  try {
    return JSON.parse(value) as T;
  } catch {
    return fallback;
  }
}
